import type { PolicyTemplate } from '@abs/shared-types';
import { PolicyRuleType, SessionType } from '@abs/shared-types';

/**
 * Pre-configured attendance policy templates for quick course setup.
 * Each template provides a complete policy configuration minus id and name.
 */
export const POLICY_TEMPLATES: PolicyTemplate[] = [
  {
    id: 'standard_3_miss',
    name: 'Standard 3-Miss Policy',
    description: 'Fixed-count policy allowing 3 absences. Each 2 additional misses drop one letter grade.',
    policy: {
      ruleType: PolicyRuleType.FixedCount,
      attendanceFloor: 9,
      attendanceFloorUnit: 'count',
      tiers: [
        {
          id: 't-3miss-1',
          absenceThreshold: 3,
          gradeDeduction: 0,
          letterGradeDrop: 0,
          description: '3rd absence: grace period ends',
        },
        {
          id: 't-3miss-2',
          absenceThreshold: 5,
          gradeDeduction: 5,
          letterGradeDrop: 1,
          description: '5th absence: −1 letter grade',
        },
        {
          id: 't-3miss-3',
          absenceThreshold: 7,
          gradeDeduction: 10,
          letterGradeDrop: 2,
          description: '7th absence: −2 letter grades',
        },
        {
          id: 't-3miss-4',
          absenceThreshold: 9,
          gradeDeduction: 15,
          letterGradeDrop: 3,
          description: '9th absence: auto-fail',
        },
      ],
      grace: { unexcusedGrace: 0, excusedGrace: 0 },
      tardyConversion: { enabled: false, tardiesPerAbsence: 3 },
      excusedAbsenceLimit: null,
      partialAbsenceValue: 1.0,
      sessionBasis: SessionType.ClassMeeting,
    },
  },
  {
    id: 'percent_80_floor',
    name: '80% Attendance Floor',
    description: 'Percentage-based policy requiring 80% attendance. Auto-fail below 80%.',
    policy: {
      ruleType: PolicyRuleType.PercentageBased,
      attendanceFloor: 80,
      attendanceFloorUnit: 'percentage',
      tiers: [
        {
          id: 't-80-1',
          absenceThreshold: 3,
          gradeDeduction: 3,
          letterGradeDrop: 0,
          description: '3 absences: −3 points',
        },
        {
          id: 't-80-2',
          absenceThreshold: 5,
          gradeDeduction: 7,
          letterGradeDrop: 1,
          description: '5 absences: −7 points (≈1 letter)',
        },
        {
          id: 't-80-3',
          absenceThreshold: 7,
          gradeDeduction: 12,
          letterGradeDrop: 2,
          description: '7 absences: −12 points (≈2 letters)',
        },
      ],
      grace: { unexcusedGrace: 1, excusedGrace: 2 },
      tardyConversion: { enabled: false, tardiesPerAbsence: 3 },
      excusedAbsenceLimit: null,
      partialAbsenceValue: 1.0,
      sessionBasis: SessionType.ClassMeeting,
    },
  },
  {
    id: 'per_absence_points',
    name: 'Per-Absence Point Deduction',
    description: 'Each unexcused absence deducts 2 points from the base grade. No tier thresholds.',
    policy: {
      ruleType: PolicyRuleType.PerAbsenceDeduction,
      attendanceFloor: 50,
      attendanceFloorUnit: 'percentage',
      tiers: [
        {
          id: 't-per-1',
          absenceThreshold: 1,
          gradeDeduction: 2,
          letterGradeDrop: 0,
          description: 'Each absence: −2 points',
        },
      ],
      grace: { unexcusedGrace: 0, excusedGrace: 1 },
      tardyConversion: { enabled: false, tardiesPerAbsence: 3 },
      excusedAbsenceLimit: null,
      partialAbsenceValue: 1.0,
      sessionBasis: SessionType.ClassMeeting,
    },
  },
  {
    id: 'strict_lab',
    name: 'Strict Lab Policy',
    description: 'Zero tolerance for lab sessions. Any unexcused absence results in auto-fail.',
    policy: {
      ruleType: PolicyRuleType.FixedCount,
      attendanceFloor: 1,
      attendanceFloorUnit: 'count',
      tiers: [
        {
          id: 't-lab-1',
          absenceThreshold: 1,
          gradeDeduction: 100,
          letterGradeDrop: 4,
          description: '1st absence: auto-fail lab',
        },
      ],
      grace: { unexcusedGrace: 0, excusedGrace: 0 },
      tardyConversion: { enabled: false, tardiesPerAbsence: 2 },
      excusedAbsenceLimit: 0,
      partialAbsenceValue: 1.0,
      sessionBasis: SessionType.Lab,
    },
  },
  {
    id: 'generous_grace',
    name: 'Generous with Grace',
    description: 'Fixed-count allowing 5 absences with 2 free grace days. Escalating penalties at 3, 5, and 7.',
    policy: {
      ruleType: PolicyRuleType.FixedCount,
      attendanceFloor: 10,
      attendanceFloorUnit: 'count',
      tiers: [
        {
          id: 't-gen-1',
          absenceThreshold: 3,
          gradeDeduction: 2,
          letterGradeDrop: 0,
          description: '3rd absence: −2 points (warning)',
        },
        {
          id: 't-gen-2',
          absenceThreshold: 5,
          gradeDeduction: 5,
          letterGradeDrop: 1,
          description: '5th absence: −5 points (1 letter drop)',
        },
        {
          id: 't-gen-3',
          absenceThreshold: 7,
          gradeDeduction: 10,
          letterGradeDrop: 2,
          description: '7th absence: −10 points (2 letter drops)',
        },
        {
          id: 't-gen-4',
          absenceThreshold: 10,
          gradeDeduction: 100,
          letterGradeDrop: 4,
          description: '10th absence: auto-fail',
        },
      ],
      grace: { unexcusedGrace: 2, excusedGrace: 3 },
      tardyConversion: { enabled: true, tardiesPerAbsence: 3 },
      excusedAbsenceLimit: null,
      partialAbsenceValue: 1.0,
      sessionBasis: SessionType.ClassMeeting,
    },
  },
  {
    id: 'tardy_heavy',
    name: 'Tardy-Heavy Policy',
    description: '4 absences allowed. 3 tardies count as 1 full absence. Designed for punctuality emphasis.',
    policy: {
      ruleType: PolicyRuleType.FixedCount,
      attendanceFloor: 8,
      attendanceFloorUnit: 'count',
      tiers: [
        {
          id: 't-tardy-1',
          absenceThreshold: 4,
          gradeDeduction: 0,
          letterGradeDrop: 0,
          description: '4th absence: limit reached',
        },
        {
          id: 't-tardy-2',
          absenceThreshold: 6,
          gradeDeduction: 5,
          letterGradeDrop: 1,
          description: '6th absence: −1 letter grade',
        },
        {
          id: 't-tardy-3',
          absenceThreshold: 8,
          gradeDeduction: 15,
          letterGradeDrop: 3,
          description: '8th absence: auto-fail',
        },
      ],
      grace: { unexcusedGrace: 0, excusedGrace: 1 },
      tardyConversion: { enabled: true, tardiesPerAbsence: 3 },
      excusedAbsenceLimit: null,
      partialAbsenceValue: 1.0,
      sessionBasis: SessionType.ClassMeeting,
    },
  },
  {
    id: 'discussion_90',
    name: 'Discussion Section (90%)',
    description: 'Percentage-based requiring 90% attendance for discussion sections. Strict participation policy.',
    policy: {
      ruleType: PolicyRuleType.PercentageBased,
      attendanceFloor: 90,
      attendanceFloorUnit: 'percentage',
      tiers: [
        {
          id: 't-disc-1',
          absenceThreshold: 2,
          gradeDeduction: 3,
          letterGradeDrop: 0,
          description: '2 absences: −3 points',
        },
        {
          id: 't-disc-2',
          absenceThreshold: 3,
          gradeDeduction: 7,
          letterGradeDrop: 1,
          description: '3 absences: −7 points (1 letter)',
        },
        {
          id: 't-disc-3',
          absenceThreshold: 4,
          gradeDeduction: 12,
          letterGradeDrop: 2,
          description: '4 absences: −12 points (2 letters)',
        },
      ],
      grace: { unexcusedGrace: 0, excusedGrace: 1 },
      tardyConversion: { enabled: true, tardiesPerAbsence: 2 },
      excusedAbsenceLimit: 2,
      partialAbsenceValue: 0.5,
      sessionBasis: SessionType.Discussion,
    },
  },
];
